import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SelectInputMulti } from '@uhg-abyss/web/ui/SelectInputMulti';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { Label } from '@uhg-abyss/web/ui/Label';

import { styled } from '@uhg-abyss/web/tools/styled';
import { DateInput } from '@uhg-abyss/web/ui/DateInput';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { config } from '@uhg-abyss/web/tools/config';
import { useCalendarApi } from '@src/hooks/useCalendarApi';
import { ErrorComponent } from '@src/common/Error';
import { LagoonContext } from '@src/AppContext';

const Wrapper = styled('div', {
  '.abyss-descriptors-error-message': {
    fontSize: '13px ',
    fontWeight: '400',
  },
});

const FlexDisplay = styled('div', {
  display: 'flex',
  gap: '20px 4%',
  alignItems: 'baseline',
  flexWrap: 'wrap',

  '@screen < $sm': {
    gap: '20px 0',
  },
});

const FlexItem = styled('div', {
  flexBasis: '48%',
  '@screen < $sm': {
    flexBasis: '100%',
  },
});

const FlexItemApi = styled('div', {
  flexBasis: '100%',
  '@screen < $sm': {
    flexBasis: '100%',
  },
});

const TextAreaStyles = styled('div', {
  paddingTop: '20px',
  paddingBottom: '20px',
});

const RequiredLabel = styled('div', {
  fontSize: '15px',
  fontWeight: '600',
  marginBottom: '24px',
});

const FullNameText = styled('span', {
  color: '#424242',
  fontSize: '14px',
  lineHeight: '18px',
  fontWeight: 400,
  verticalAlign: '-webkit-baseline-middle',
});
export const FormFields = () => {
  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });
  const dsUserInfo = useSelector((state) => {
    return state.GET_DS_USER_INFO.data;
  });
  const holidayDateApi = useSelector((state) => {
    return state.GET_CALENDAR_HOLIDAY.data;
  });
  const form = useForm();
  const isFQHC = form.getValues('isFQHC');
  const orgType = form.getValues('orgType');
  const resourceAvailable = form.getValues('isItResourceAvailable');
  const resourceType = form.getValues('itResourceType');
  const [calendarDateApi, setCalendarDate] = useCalendarApi((state) => {
    return state.GET_CALANDAR_TIMES;
  });
  const [preferredConsultant, setPreferredConsultant] = useState('');
  const [selectedOrgType, setSelectedOrgType] = useState('');
  const [itResourceAvailable, setItResourceAvailable] = useState('');
  const [itResourceType, setItResourceType] = useState('');
  const [swaggerOptions, setSwaggerOptions] = useState([]);
  const [consultantOptions, setConsultantOptions] = useState([]);
  const { nextWorkingDay, lastAllowedWorkingDay } = holidayDateApi || {};
  const lagoonContext = React.useContext(LagoonContext);
  const apisData = lagoonContext
    ?.getTable('outlook-integrations-apis')
    .getRow();
  const featureToggles = lagoonContext?.getTable('feature-toggles');
  const lagoonConfigs = lagoonContext?.getTable('configs');
  const dentalConsultantIds = lagoonConfigs?.getRow('DENTAL_CON_IDS')
    ? lagoonConfigs?.getRow('DENTAL_CON_IDS')?.value
    : config('DENTAL_CON_IDS');
  const disableScheduling =
    featureToggles?.getRow('DISABLE_SCHEDULING')?.enabled;

  useEffect(() => {
    // if a new consultant is selected, clear the meeting times for the previous one
    form.setValue('preferredMeetingTime', '');
  }, [preferredConsultant, isFQHC]);

  useEffect(() => {
    form.setValue('emrVendor', '');
    form.setValue('rcmSupportedServices', []);
    form.setValue('automations', '');
  }, [selectedOrgType]);

  useEffect(() => {
    form.setValue('itResourceType', '');
  }, [itResourceAvailable]);

  useEffect(() => {
    form.setValue('itCompanyName', '');
  }, [itResourceType, itResourceAvailable]);

  useEffect(() => {
    form.setValue('consultant', '');
  }, [isFQHC]);

  const validateDate = (date) => {
    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    return dateRegex.test(date);
  };

  const callCalendarAPI = (date) => {
    if (validateDate(date)) {
      setCalendarDate({ userInfo, date });
    }
  };

  const updateSwaggerOptions = (options) => {
    function getRaw(s) {
      return s.replace(/[&/\\#,+()$~%!.„'":*‚^_¤?<>|@ª{«»§}©®™ ]/g, '').trim();
    }
    const sortedOptions = options?.sort((a, b) => {
      return getRaw(a.section.toLowerCase()) < getRaw(b.section.toLowerCase())
        ? -1
        : 1;
    });
    setSwaggerOptions(sortedOptions);
  };

  const subCategories = (title, endpointList) => {
    const finalList = [];
    const list = endpointList.split(', ');
    list.forEach((item) => {
      const obj = {
        value: `${item}`,
        label: `${item}`,
      };
      finalList.push(obj);
    });
    return finalList;
  };

  const obj = apisData?.map((item) => {
    return {
      section: item.api,
      items: subCategories(item.api, item.endpoints),
    };
  });
  useEffect(() => {
    updateSwaggerOptions(obj);
  }, []);

  const isConnected =
    (dsUserInfo?.status && dsUserInfo?.status === 'ACTIVE') ||
    dsUserInfo?.status === 'PENDING' ||
    dsUserInfo?.status === 'DENIED';

  const consultantData = () => {
    const consultantsList = [];

    let availableConsultant = calendarDateApi?.data?.consultants || [];
    const selectedAPIs = form.getValues('selectedAPIs') || [];
    const isDentalApiSelected =
      Array.isArray(selectedAPIs) && selectedAPIs.includes('Dental API');
    // If Dental API is selected, filter consultants to only include 'Staci'
    const dentalConIdsArray = dentalConsultantIds?.split(',').map((id) => {
      return parseInt(id.trim(), 10);
    }); // Convert string to array of integers with radix
    if (isDentalApiSelected) {
      setPreferredConsultant('');
      availableConsultant = availableConsultant.filter((consultant) => {
        return dentalConIdsArray?.some((id) => {
          return consultant?.consultantId === id;
        });
      });
    }

    availableConsultant.forEach((element) => {
      consultantsList.push({
        label: `${element?.firstName} ${element?.lastNameInitials}`,
        value: element.consultantId,
      });
    });
    setConsultantOptions(consultantsList);
  };

  useEffect(() => {
    // if a new date is selected, clear the FQHC question
    form.setValue('isFQHC', '');
    form.setValue('consultant', '');
    consultantData();
  }, [calendarDateApi]);

  const consultantTime = () => {
    const userSelectedConsultant = form.getValues('consultant');
    const consultantAvailableTimes = calendarDateApi?.data?.consultants?.find(
      (consultant) => {
        return consultant?.consultantId === userSelectedConsultant;
      }
    )?.availableTimes;

    const consultantsAvailableTimeOptions = consultantAvailableTimes?.map(
      (time) => {
        return {
          label: time,
          value: time,
        };
      }
    );

    return consultantsAvailableTimeOptions;
  };

  const unavailableCalendarDates = () => {
    if (nextWorkingDay) {
      return new Date(nextWorkingDay);
    }
    // if holidayDateApi is failing, use the next day as next available date
    return new Date();
  };

  const apiCalendarMaximumDate = () => {
    if (lastAllowedWorkingDay) {
      return new Date(lastAllowedWorkingDay);
    }
    // fallback scenario
    const maximumDate = new Date();
    maximumDate.setDate(maximumDate.getDate() + 30);

    return maximumDate;
  };

  const isHoliday = (excludeDate) => {
    return holidayDateApi?.holidays?.some((holiday) => {
      const excludeDateConfigured = new Date(excludeDate).toLocaleDateString(
        'en-US',
        {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }
      );
      return holiday === excludeDateConfigured;
    });
  };

  const symbol = () => {
    return (
      <span style={{ color: '#C40000', fontSize: '27px', fontWeight: '200' }}>
        *
      </span>
    );
  };

  return (
    <Wrapper>
      <RequiredLabel>Required {symbol()}</RequiredLabel>
      <FlexDisplay>
        {!isConnected && (
          <FlexItem>
            <TextInput
              id="orgName"
              label="Organization Name"
              type="text"
              model="orgName"
              isClearable
              validators={{ required: true }}
              css={{
                'abyss-text-input-label': {
                  position: 'relative',
                  fontSize: '15px !important',
                },
              }}
            />
          </FlexItem>
        )}
        <FlexItem id="fullName">
          <Label css={{ fontSize: '15px' }}>Full Name</Label>
          <FullNameText>{`${userInfo.firstName} ${
            userInfo.lastName || userInfo.family_name
          }`}</FullNameText>
        </FlexItem>
        {!isConnected && (
          <React.Fragment>
            <FlexItem>
              <SelectInput
                id="orgType"
                label="Organization Type"
                model="orgType"
                placeholder="Choose an option"
                isClearable
                isSearchable
                options={[
                  { label: 'Provider', value: 'Provider' },
                  {
                    label: 'Revenue Cycle Management (RCM)',
                    value: 'Revenue Cycle Management (RCM)',
                  },
                  { label: 'Clearinghouse', value: 'Clearinghouse' },
                  {
                    label: 'FQHC, RHC or Tribal Group',
                    value: 'FQHC, RHC or Tribal Group',
                  },
                  { label: 'Other', value: 'Other' },
                ]}
                validators={{ required: true }}
                onChange={setSelectedOrgType}
                css={{
                  'abyss-select-input-label': {
                    position: 'relative',
                    fontSize: '15px',
                  },
                }}
              />
            </FlexItem>
            <FlexItem>
              <TextInput
                id="orgWebsite"
                label="Organization Website"
                type="text"
                model="orgWebsite"
                isClearable
                css={{
                  'abyss-text-input-label': {
                    position: 'relative',
                    fontSize: '15px',
                  },
                }}
              />
            </FlexItem>
          </React.Fragment>
        )}
        {!isConnected && orgType === 'Provider' && (
          <SelectInput
            id="emrVendor"
            label="If you use an EMR, please let us know which system your team uses from the dropdown menu. (Required for providers)"
            model="emrVendor"
            placeholder="Choose an option"
            isClearable
            isSearchable
            options={[
              { label: 'Athena', value: 'Athena' },
              {
                label: 'Centricity',
                value: 'Centricity',
              },
              { label: 'Cerner', value: 'Cerner' },
              { label: 'ECW', value: 'ECW' },
              { label: 'Epic', value: 'Epic' },
              {
                label: 'NextGen',
                value: 'NextGen',
              },
              { label: 'VER', value: 'VER' },
              { label: 'Other', value: 'Other' },
            ]}
            validators={{ required: true }}
            css={{
              'abyss-select-input-label': {
                position: 'relative',
                fontSize: '15px',
              },
              'abyss-select-input-input-container': {
                width: '48%',
              },
            }}
          />
        )}
        {!isConnected && orgType === 'Revenue Cycle Management (RCM)' && (
          <SelectInputMulti
            id="rcmSupportedServices"
            label="If you are a vendor or RCM, what services do you support within your organization?"
            model="rcmSupportedServices"
            placeholder="Choose an option"
            isClearable
            isSearchable
            options={[
              {
                label: 'Claims Status and Payment',
                value: 'Claims Status and Payment',
              },
              {
                label: 'Claims Submission',
                value: 'Claims Submission',
              },
              {
                label: 'Claims Appeals/Reconsiderations',
                value: 'Claims Appeals/Reconsiderations',
              },
              { label: 'Documents', value: 'Documents' },
              {
                label: 'Prior Authorizations and Notifications ',
                value: 'Prior Authorizations and Notifications ',
              },
              {
                label: 'Eligibility and Benefits',
                value: 'Eligibility and Benefits',
              },
              {
                label: 'Provider Demographics',
                value: 'Provider Demographics',
              },
              { label: 'Referrals', value: 'Referrals' },
              { label: 'Other', value: 'Other' },
              { label: 'N/A', value: 'N/A' },
            ]}
            validators={{ required: true }}
            css={{
              'abyss-select-input-label': {
                position: 'relative',
                fontSize: '15px',
              },
              'abyss-select-input-multi-input': {
                width: '48%',
              },
            }}
          />
        )}
        <FlexItem>
          <TextInput
            id="email"
            label="Email"
            type="text"
            model="email"
            isClearable
            validators={{
              required: true,
              pattern: {
                // eslint-disable-next-line no-control-regex
                value:
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: 'Invalid email format.',
              },
            }}
            css={{
              'abyss-text-input-label': {
                position: 'relative',
                fontSize: '15px',
              },
            }}
          />
        </FlexItem>
        <FlexItem>
          <TextInput
            id="phone"
            label="Phone Number"
            model="phone"
            isClearable
            mask="phone"
            css={{
              'abyss-text-input-label': {
                position: 'relative',
                fontSize: '15px',
              },
            }}
          />
        </FlexItem>
        <FlexItemApi>
          <SelectInputMulti
            id="selectedAPIs"
            label="What API solution(s) are you interested in?"
            model="selectedAPIs"
            placeholder="Choose an option"
            isClearable
            isSearchable
            onChange={(selectedValues) => {
              consultantData(); // Update consultants when APIs are selected
              if (selectedValues.includes('Dental API')) {
                form.setValue('consultant', ''); // Reset consultant field
              }
            }}
            options={swaggerOptions}
            validators={{ required: true }}
            maxListHeight="290px"
            css={{
              'abyss-select-input-multi-label': {
                position: 'relative',
                fontSize: '15px',
              },
            }}
          />
        </FlexItemApi>
        {!isConnected && (
          <RadioGroup
            label="Do you have IT resources to help integrate our API services into your systems?"
            model="isItResourceAvailable"
            display="row"
            validators={{ required: true }}
            onChange={(e) => {
              return setItResourceAvailable(e.target.value);
            }}
            css={{
              'abyss-radio-group-label': {
                position: 'relative',
                fontSize: '15px',
              },
            }}
          >
            <RadioGroup.Radio label="Yes" value="Yes" />
            <RadioGroup.Radio label="No" value="No" />
          </RadioGroup>
        )}
        {!isConnected && resourceAvailable === 'Yes' && (
          <FlexItem>
            <SelectInput
              id="itResourceType"
              label="Are your IT resources internal or outsourced?"
              model="itResourceType"
              placeholder="Choose an option"
              isClearable
              isSearchable
              onChange={setItResourceType}
              options={[
                { label: 'Internal', value: 'Internal' },
                {
                  label: 'Outsourced',
                  value: 'Outsourced',
                },
              ]}
              css={{
                'abyss-select-input-label': {
                  position: 'relative',
                  fontSize: '15px',
                },
              }}
            />
          </FlexItem>
        )}
        {!isConnected &&
          resourceType === 'Outsourced' &&
          resourceAvailable === 'Yes' && (
            <FlexItem>
              <TextInput
                id="itCompanyName"
                label="If outsourced, Which company do you use?"
                type="text"
                model="itCompanyName"
                isClearable
                css={{
                  'abyss-text-input-label': {
                    position: 'relative',
                    fontSize: '15px',
                  },
                }}
              />
            </FlexItem>
          )}
        {!isConnected && (
          <React.Fragment>
            <SelectInput
              id="timeline"
              label="What is your timeline for automating workflow with an API solution?"
              model="timeline"
              placeholder="Choose an option"
              isClearable
              isSearchable
              options={[
                { label: 'As soon as possible', value: 'As soon as possible' },
                {
                  label: 'Within 30 Days',
                  value: 'Within 30 Days',
                },
                {
                  label: 'Within 60 Days',
                  value: 'Within 60 Days',
                },
                {
                  label: 'More than 60 Days ',
                  value: 'More than 60 Days ',
                },
              ]}
              validators={{ required: true }}
              css={{
                'abyss-select-input-label': {
                  position: 'relative',
                  fontSize: '15px',
                },
                'abyss-select-input-input-container': {
                  width: '48%',
                },
              }}
            />
            <SelectInput
              id="tinCount"
              label="How many Tax IDs (TINs) does your organization work with?"
              model="tinCount"
              placeholder="Choose an option"
              isClearable
              isSearchable
              validators={{ required: true }}
              options={[
                { label: '1', value: '1' },
                {
                  label: '2-10',
                  value: '2-10',
                },
                {
                  label: '11-50',
                  value: '11-50',
                },
                {
                  label: '51-100',
                  value: '51-100',
                },
                {
                  label: '100+',
                  value: '100+',
                },
              ]}
              css={{
                'abyss-select-input-label': {
                  position: 'relative',
                  fontSize: '15px',
                },
                'abyss-select-input-input-container': {
                  width: '48%',
                },
              }}
            />
            <FlexItem>
              <TextInput
                id="primaryTaxId"
                label="What is your primary Tax ID?"
                model="primaryTaxId"
                maxLength={9}
                isClearable
                type="text"
                pattern="\d*"
                validators={{
                  required: true,
                  pattern: {
                    value: /^\d+$/,
                    message: 'Tax ID must contain only numbers',
                  },
                  minLength: {
                    value: 9,
                    message: 'Tax ID must be at least 9 characters long',
                  },
                }}
                css={{
                  'abyss-text-input-label': {
                    position: 'relative',
                    fontSize: '15px',
                  },
                }}
              />
            </FlexItem>
            <FlexItem />
          </React.Fragment>
        )}
        {!isConnected &&
          (orgType === 'Revenue Cycle Management (RCM)' ||
            orgType === 'Clearinghouse') && (
            <SelectInput
              id="automations"
              label="Do you currently use any automation transactions in UHC Provider Portal? (e.g., BOTS)"
              model="automations"
              placeholder="Choose an option"
              isClearable
              isSearchable
              options={[
                { label: 'Yes', value: 'Yes' },
                {
                  label: 'No',
                  value: 'No',
                },
                {
                  label: "I don't know",
                  value: "I don't know",
                },
              ]}
              css={{
                'abyss-select-input-label': {
                  position: 'relative',
                  fontSize: '15px',
                },
                'abyss-select-input-input-container': {
                  width: '48%',
                },
              }}
            />
          )}
        {!disableScheduling && (
          <FlexItem>
            <DateInput
              id="preferredMeetingDate"
              onChange={callCalendarAPI}
              model="preferredMeetingDate"
              minimumDate={unavailableCalendarDates()}
              maximumDate={apiCalendarMaximumDate()}
              excludeDate={(excludeDate) => {
                return (
                  excludeDate.getDay() === 0 ||
                  excludeDate.getDay() === 6 ||
                  isHoliday(excludeDate)
                );
              }}
              label="Preferred meeting date"
              validators={{ required: true }}
              subText={
                <span
                  style={{
                    fontWeight: '1',
                    color: '#7d7f81',
                    'font-size': '13px',
                  }}
                >
                  Date Format: mm/dd/yyyy
                </span>
              }
              css={{
                'abyss-date-picker-input-label': {
                  position: 'relative',
                  fontSize: '15px',
                },
              }}
            />
            {calendarDateApi?.error ? (
              <ErrorComponent>{calendarDateApi?.data?.message}</ErrorComponent>
            ) : null}
          </FlexItem>
        )}

        {isConnected && (
          <RadioGroup
            label="Are you designated as a FQHC, RHC, or Tribal group?"
            model="isFQHC"
            display="row"
            isDisabled={
              !disableScheduling &&
              (!form.getValues('preferredMeetingDate') ||
                !calendarDateApi?.data ||
                !Object.keys(calendarDateApi?.data)?.length)
            }
            validators={{ required: true }}
            css={{
              'abyss-radio-group-label': {
                position: 'relative',
                fontSize: '15px',
              },
            }}
          >
            <RadioGroup.Radio label="Yes" value="yes" />
            <RadioGroup.Radio label="No" value="no" />
          </RadioGroup>
        )}
        {!disableScheduling && (
          <React.Fragment>
            <FlexItem>
              <SelectInput
                id="consultant"
                label="Preferred consultant"
                subText={
                  <span
                    style={{
                      fontWeight: '1',
                      color: '#7d7f81',
                      fontSize: '13px',
                    }}
                  >
                    If there are no consultants available or you don't see your
                    preferred consultant's name, please select a different date.
                  </span>
                }
                model="consultant"
                isDisabled={
                  !form.getValues('preferredMeetingDate') ||
                  (isConnected && !form.getValues('isFQHC')) ||
                  !calendarDateApi?.data ||
                  !Object.keys(calendarDateApi?.data)?.length
                }
                validators={{ required: true }}
                onChange={setPreferredConsultant}
                options={consultantOptions}
                placeholder="Select a consultant"
                css={{
                  'abyss-select-input-label': {
                    position: 'relative',
                    fontSize: '15px',
                  },
                }}
              />
            </FlexItem>
            <FlexItem>
              <SelectInput
                id="preferredMeetingTime"
                label="Preferred meeting time of day"
                model="preferredMeetingTime"
                isDisabled={
                  !form.getValues('consultant') ||
                  !calendarDateApi?.data ||
                  !Object.keys(calendarDateApi?.data)?.length
                }
                validators={{ required: true }}
                options={consultantTime()}
                placeholder="Select a meeting time"
                subText={
                  <span
                    style={{
                      fontWeight: '1',
                      color: '#7d7f81',
                      'font-size': '13px',
                    }}
                  >
                    Eastern Time Zone (ET)
                  </span>
                }
                css={{
                  'abyss-select-input-label': {
                    position: 'relative',
                    fontSize: '15px',
                  },
                  'abyss-descriptors-sub-text': {
                    fontSize: '10px',
                  },
                }}
              />
            </FlexItem>
          </React.Fragment>
        )}
      </FlexDisplay>
      {isConnected && (
        <TextAreaStyles>
          <TextInputArea
            id="businessValue"
            maxLength={500}
            label="Business Value"
            model="businessValue"
            placeholder="Please explain your intended use for this API."
            validators={{ required: true }}
            className="abyss-label-font"
            css={{
              'abyss-text-input-area-label': {
                position: 'relative',
                fontSize: '15px',
              },
            }}
          />
        </TextAreaStyles>
      )}
    </Wrapper>
  );
};

FormFields.propTypes = {};
